import {
  sendAuthenticatedUpload,
  handleAuthenticationError,
} from '../../../../util/sessionExpiry';
import { ConfigContext } from '../../../../providers/ConfigContext';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { FileState, uploadStateReducer } from '../../reducer';
import { UploadScope } from '../../types';

export const useAttachmentUpload = (scope: UploadScope) => {
  const config = ConfigContext.useContainer();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadState, dispatch] = useReducer(uploadStateReducer, []);

  useEffect(() => {
    if (uploadState.length && uploadState.every(fileIsDoneUploading)) {
      setIsUploading(false);
    }
    if (uploadState.some(fileIsUploading)) {
      setIsUploading(true);
    }
  }, [uploadState]);

  const attachmentsUrl = scope.community
    ? `${config?.aws.attachments.gateway}/community/attachments`
    : `${config?.aws.attachments.gateway}/attachments`;

  const uploadFile = useCallback(
    (fileState: FileState) => {
      dispatch({ type: 'UPLOADING', id: fileState.id });

      if (config?.aws.attachments.gateway) {
        const xhr = new XMLHttpRequest();

        xhr.open('POST', attachmentsUrl);
        xhr.setRequestHeader(
          'Content-Type',
          fileState.file.type || 'application/octet-stream',
        );
        xhr.setRequestHeader('Application', scope.application);
        xhr.setRequestHeader('Parent-Id', scope.parentId);
        xhr.setRequestHeader('Parent-Type', scope.parentType);
        xhr.setRequestHeader('File-Name', fileState.name);

        xhr.addEventListener('error', () => {
          dispatch({ type: 'UPLOAD_FAILURE', id: fileState.id });
        });
        xhr.addEventListener('load', () => {
          if (xhr.status === 401) {
            handleAuthenticationError({ status: 401 });
            return;
          }
          if (xhr.status < 200 || xhr.status >= 300) {
            dispatch({ type: 'UPLOAD_FAILURE', id: fileState.id });
            return;
          }
          dispatch({ type: 'UPLOAD_SUCCESS', id: fileState.id });
        });
        sendAuthenticatedUpload(xhr, fileState.file, () => {
          dispatch({ type: 'UPLOAD_FAILURE', id: fileState.id });
        });
      }
    },
    [config, scope, attachmentsUrl],
  );

  const uploadFiles = useCallback(() => {
    uploadState
      .filter((item) => item.status === 'READY')
      .forEach((state) => uploadFile(state));
  }, [uploadState, uploadFile]);

  const addFiles = useCallback((files: File[]) => {
    dispatch({ type: 'ADD_FILES', files });
  }, []);

  const removeFile = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_FILE', id });
  }, []);

  const setFileName = useCallback((id: string, name: string) => {
    dispatch({ type: 'SET_FILE_NAME', id, name });
  }, []);

  return {
    isUploading,
    uploadFiles,
    uploadState,
    addFiles,
    removeFile,
    setFileName,
  };
};

function fileIsUploading(state: FileState): boolean {
  return state.status === 'UPLOADING';
}

function fileIsDoneUploading(state: FileState): boolean {
  return state.status === 'UPLOAD_FAILURE' || state.status === 'UPLOAD_SUCCESS';
}
